// ===================================================================
// SHELTER MANAGEMENT — Module Layout with grouped sub-navigation
// ===================================================================

import { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import {
  IconLayoutDashboard, IconBuildingCommunity, IconUserPlus, IconUsers,
  IconUsersGroup, IconQrcode, IconStethoscope, IconTransfer,
  IconPackage, IconDog, IconCar, IconAlertTriangle,
  IconExternalLink, IconMapPin,
  IconSpeakerphone, IconClipboardList, IconBed, IconHeart, IconLibrary,
  IconDeviceMobile, IconCheck, IconCopy,
} from '@tabler/icons-react';
import { Group, Tooltip, CopyButton, ActionIcon, Button } from '../../components/gab/GabComponents';
import { useFilteredNav } from '../../hooks/useFilteredNav';
import type { NavItem } from '../../types/nav';
import ModuleNav from '../../components/layout/ModuleNav';
import PageHeader from '../../components/ui/PageHeader';
import { useAuthStore } from '../../auth/auth-store';
import { useShelterStore } from '../../store/shelter-store';

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard',      path: '/shelter',               icon: IconLayoutDashboard, exact: true },
  { label: 'Shelters',       path: '/shelter/shelters',      icon: IconBuildingCommunity },
  { label: 'Intake',         path: '/shelter/intake',        icon: IconUserPlus },
  { label: 'Survivors',      path: '/shelter/survivors',     icon: IconUsers },
  { label: 'Families',       path: '/shelter/families',      icon: IconUsersGroup },
  { label: 'Pets',           path: '/shelter/pets',          icon: IconDog },
  { label: 'Vehicles',       path: '/shelter/vehicles',      icon: IconCar },
  { label: 'Lookup',         path: '/shelter/lookup',        icon: IconQrcode },
  { label: 'Medical',        path: '/shelter/medical',       icon: IconStethoscope, minLevel: 'editor' },
  { label: 'Transfers',      path: '/shelter/transfers',     icon: IconTransfer },
  { label: 'Property',       path: '/shelter/property',      icon: IconPackage },
  { label: 'Supplies',       path: '/shelter/supplies',      icon: IconBed },
  { label: 'Distribution',   path: '/shelter/distribution',  icon: IconPackage },
  { label: 'Triage Setup',   path: '/shelter/triage',        icon: IconClipboardList, minLevel: 'editor' },
  { label: 'Incidents',      path: '/shelter/incidents',     icon: IconAlertTriangle },
  { label: 'Referrals',      path: '/shelter/referrals',     icon: IconExternalLink },
  { label: 'Reunification',  path: '/shelter/reunification', icon: IconHeart },
  { label: 'Comms',          path: '/shelter/comms',         icon: IconSpeakerphone, minLevel: 'editor' },
  { label: 'Map',            path: '/shelter/map',           icon: IconMapPin },
  { label: 'Facility Library', path: '/shelter/facility-library', icon: IconLibrary, minLevel: 'editor' },
];

// Group nav items — unnamed group renders as individual buttons, named groups as dropdowns
const NAV_GROUPS: { label: string; paths: string[] }[] = [
  { label: '', paths: ['/shelter', '/shelter/shelters'] },
  { label: 'People', paths: ['/shelter/intake', '/shelter/survivors', '/shelter/families', '/shelter/lookup'] },
  { label: 'Assets', paths: ['/shelter/pets', '/shelter/vehicles', '/shelter/property', '/shelter/supplies', '/shelter/distribution'] },
  { label: 'Operations', paths: ['/shelter/medical', '/shelter/transfers', '/shelter/triage', '/shelter/incidents', '/shelter/referrals', '/shelter/reunification'] },
  { label: 'Admin', paths: ['/shelter/comms', '/shelter/map', '/shelter/facility-library'] },
];

const shelterPortalUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/sheltermanagement`;

function ShelterPortalButton() {
  return (
    <Group gap={4}>
      <Tooltip label="Open field shelter portal" withArrow>
        <Button variant="light" color="var(--color-primary-700)" size="xs"
          leftSection={<IconDeviceMobile size={14} />}
          component="a" href={shelterPortalUrl} target="_blank">
          Field Portal
        </Button>
      </Tooltip>
      <CopyButton value={shelterPortalUrl}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? 'Copied!' : 'Copy portal link'} withArrow>
            <ActionIcon variant="light" color={copied ? 'var(--color-success)' : 'var(--color-primary-700)'} size="sm" onClick={copy}>
              {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
            </ActionIcon>
          </Tooltip>
        )}
      </CopyButton>
    </Group>
  );
}

export default function ShelterLayout() {
  const { pathname } = useLocation();
  const visibleItems = useFilteredNav('shelter', NAV_ITEMS);

  // Re-fetch core shelter data whenever the active incident changes.
  // Using Zustand's subscribe (outside React's render cycle) so it fires
  // immediately when setActiveIncident is called, without depending on
  // useEffect re-run detection.
  const prevIncidentId = useRef<number | null | undefined>(undefined);
  useEffect(() => {
    // Run once on mount to capture current incident
    prevIncidentId.current = useAuthStore.getState().activeIncident?.id ?? null;

    const unsub = useAuthStore.subscribe((state) => {
      const newId = state.activeIncident?.id ?? null;
      if (newId !== prevIncidentId.current) {
        prevIncidentId.current = newId;
        const store = useShelterStore.getState();
        store.fetchShelters();
        store.fetchSurvivors();
      }
    });
    return unsub;
  }, []);

  const groups = NAV_GROUPS.map(g => ({
    ...g,
    items: g.paths
      .map(p => visibleItems.find(vi => vi.path === p))
      .filter(Boolean) as typeof visibleItems,
  })).filter(g => g.items.length > 0);

  const currentPage = NAV_ITEMS.find(item =>
    item.exact ? pathname === item.path : pathname.startsWith(item.path)
  );

  return (
    <div style={{ padding: '24px' }}>
      <PageHeader
        breadcrumbs={[{ label: 'Respond' }, { label: 'Shelters' }]}
        title="Shelters"
        subtitle="Shelter operations, supplies, survivor intake and reunification"
        actions={<ShelterPortalButton />}
      />
      <ModuleNav items={visibleItems} groups={groups} />
      <Outlet />
    </div>
  );
}
